import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

export default function InvoiceRefunds({ invoice, onUpdated }) {
  const { user } = useAuth();
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ amount:'', reason:'', bank_name:'', account_number:'', account_name:'', notes:'', financial_effect:'invoice_reduction' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approval, setApproval] = useState(null);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [approvalError, setApprovalError] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [refundMethod, setRefundMethod] = useState('transfer');
  const [refundAccount, setRefundAccount] = useState('');
  useEffect(() => {
    if (approval || showForm && user?.role==='super_admin') get('/bank-accounts').then(d => setBankAccounts(d.data || []))
      .catch(err => setApprovalError(err.message));
  }, [approval,showForm,user?.role]);

  const isSuperAdmin = user?.role === 'super_admin';
  const canRequest = ['super_admin','sales_manager','content_sales','admin'].includes(user?.role);

  const load = () => {
    setLoading(true);
    get('/refunds/invoices/'+invoice.id+'/refunds')
      .then(d => setRefunds(d.data||[]))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [invoice.id]);

  const submit = async () => {
    if (!Number.isFinite(Number(form.amount)) || Number(form.amount) <= 0 || !form.reason.trim()) { setError('Amount and reason required'); return; }
    if (parseFloat(form.amount) > parseFloat(invoice.amount_paid)) {
      setError('Refund amount cannot exceed amount paid ('+fmt(invoice.amount_paid)+')');
      return;
    }
    if(isSuperAdmin&&!refundAccount){setError('Select the account used for this refund');return;}
    setSaving(true); setError('');
    try {
      await post('/refunds/invoices/'+invoice.id+'/refunds', {...form,...(isSuperAdmin?{payment_method:refundMethod,bank_account_id:refundAccount||null}:{})});
      setForm({ amount:'', reason:'', bank_name:'', account_number:'', account_name:'', notes:'', financial_effect:'invoice_reduction' });
      setShowForm(false);
      load();
      if(isSuperAdmin&&onUpdated)await onUpdated();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const handleApprove = async () => {
    if (approvalBusy) return;
    setApprovalBusy(true); setApprovalError('');
    try {
      await put('/refunds/'+approval.id+'/approve', {
        payment_method: refundMethod, bank_account_id: refundAccount || null
      });
      setApproval(null); load();
      if (onUpdated) await onUpdated();
    } catch (err) { setApprovalError(err.message); }
    finally { setApprovalBusy(false); }
  };

  const handleReject = async () => {
    try { await put('/refunds/'+rejectModal+'/reject', { rejection_reason: rejectReason }); setRejectModal(null); setRejectReason(''); load(); }
    catch (err) { alert(err.message); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this refund request?')) return;
    try { await del('/refunds/'+id); load(); }
    catch (err) { alert(err.message); }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg:'#FFF3DC', color:'#8B5E0A', label:'Pending Approval' },
      approved: { bg:'#E8F5EB', color:'#2E6B3A', label:'Approved' },
      cancelled: { bg:'#F2EFE7', color:'#3B2A21', label:'Cancelled' },
      rejected: { bg:'#FDEAEA', color:'#8F2E24', label:'Rejected' },
    };
    const s = styles[status] || styles.pending;
    return <span style={{padding:'3px 10px',borderRadius:20,fontSize:10,fontWeight:700,background:s.bg,color:s.color}}>{s.label}</span>;
  };

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div>
          <div style={{fontSize:13,fontWeight:600,color:'#3B2A21'}}>Refunds</div>
          <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>
            Amount paid: <strong>{fmt(invoice.amount_paid)}</strong> — 
            Available for refund: <strong style={{color:'#556B2F'}}>{fmt(invoice.amount_paid)}</strong>
          </div>
        </div>
        {canRequest && !invoice.refund_reconciliation_required && parseFloat(invoice.amount_paid) > 0 && !refunds.some(r=>r.status==='pending') && (
          <button className="btn btn-outline btn-sm" style={{color:'#8F2E24',borderColor:'#8F2E24'}}
            onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : isSuperAdmin?'+ Process Refund':'+ Request Refund'}
          </button>
        )}
      </div>

      {invoice.refund_reconciliation_required && <div className="alert alert-error">This invoice has an unreconciled historical refund. Its financial figures must be reconciled before further changes.</div>}
      {/* Refund request form */}
      {showForm && (
        <div style={{background:'#FFF8F0',border:'1px solid #F5B8B8',borderRadius:8,padding:16,marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:600,color:'#8F2E24',marginBottom:12}}>{isSuperAdmin?'Process Refund':'New Refund Request'}</div>
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-group">
            <label className="form-label">Financial effect *</label>
            <select className="form-control" value={form.financial_effect} onChange={e => setForm({...form, financial_effect:e.target.value})}>
              <option value="invoice_reduction">Reduce invoice price (cancelled items or reduced services)</option>
              <option value="excess_payment">Return excess payment (invoice price stays the same)</option>
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Refund Amount (₦) *</label>
              <input type="number" className="form-control" value={form.amount}
                onChange={e=>setForm({...form,amount:e.target.value})}
                placeholder="0.00" min="0.01" step="0.01" max={invoice.amount_paid} />
              <div className="form-hint">Max: {fmt(invoice.amount_paid)}</div>
            </div>
            <div className="form-group">
              <label className="form-label">Reason for Refund *</label>
              <input className="form-control" value={form.reason}
                onChange={e=>setForm({...form,reason:e.target.value})}
                placeholder="e.g. Order cancelled, wrong item..." />
            </div>
          </div>
          <div style={{fontSize:11,fontWeight:600,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.08em',margin:'12px 0 8px'}}>
            Customer Bank Details (for refund payment)
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Bank Name</label>
              <input className="form-control" value={form.bank_name}
                onChange={e=>setForm({...form,bank_name:e.target.value})}
                placeholder="e.g. GTBank" />
            </div>
            <div className="form-group">
              <label className="form-label">Account Number</label>
              <input className="form-control" value={form.account_number}
                onChange={e=>setForm({...form,account_number:e.target.value})}
                placeholder="0123456789" />
            </div>
            <div className="form-group">
              <label className="form-label">Account Name</label>
              <input className="form-control" value={form.account_name}
                onChange={e=>setForm({...form,account_name:e.target.value})}
                placeholder="Customer full name" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Additional Notes</label>
            <textarea className="form-control" rows={2} value={form.notes}
              onChange={e=>setForm({...form,notes:e.target.value})}
              placeholder="Any additional information..." />
          </div>
          {isSuperAdmin&&<div className="form-row">
            <div className="form-group"><label className="form-label">Refund payment method *</label><select className="form-control" value={refundMethod} onChange={e=>{setRefundMethod(e.target.value);setRefundAccount('');}}><option value="transfer">Bank transfer</option><option value="cash">Cash</option></select></div>
            <div className="form-group"><label className="form-label">Account used *</label><select className="form-control" value={refundAccount} onChange={e=>setRefundAccount(e.target.value)}><option value="">Select account</option>{bankAccounts.filter(a=>a.is_active!==false&&a.type===(refundMethod==='cash'?'cash':'bank')).map(a=><option key={a.id} value={a.id}>{a.name||a.account_name||a.bank_name}</option>)}</select></div>
          </div>}
          <div style={{display:'flex',gap:10}}>
            <button className="btn btn-outline btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={submit} disabled={saving}
              style={{background:'#8F2E24',borderColor:'#8F2E24'}}>
              {saving ? 'Processing...' : isSuperAdmin?'Approve & Process Refund':'Submit for Approval'}
            </button>
          </div>
        </div>
      )}

      {/* Refund list */}
      {loading ? <div className="spinner"></div> : refunds.length === 0 ? (
        <div style={{padding:'16px',textAlign:'center',background:'#F9F9F9',borderRadius:8,fontSize:12,color:'#8A8A8A'}}>
          No refund requests for this invoice.
        </div>
      ) : (
        <div>
          {refunds.map(r => (
            <div key={r.id} style={{border:'1px solid var(--border-light)',borderRadius:8,padding:14,marginBottom:10,background:'#fff'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                <div>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                    <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.3rem',fontWeight:700,color:'#8F2E24'}}>{fmt(r.amount)}</span>
                    {getStatusBadge(r.status)}
                    <span style={{fontSize:10,background:'#F2EFE7',padding:'2px 8px',borderRadius:20,color:'#556B2F',fontWeight:600}}>
                      {r.refund_type === 'full' ? 'Full Refund' : 'Partial Refund'} — {r.financial_effect === 'excess_payment' ? 'Excess payment' : r.financial_effect === 'legacy_unreconciled' ? 'Needs reconciliation' : 'Invoice reduction'}
                    </span>
                  </div>
                  <div style={{fontSize:12,color:'#3B2A21',fontWeight:500}}>{r.reason}</div>
                  <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>
                    Requested by {r.requested_by_name} on {new Date(r.created_at).toLocaleDateString('en-NG')}
                  </div>
                </div>

                {/* Actions */}
                <div style={{display:'flex',gap:6}}>
                  {isSuperAdmin && r.status==='pending' && (
                    <>
                      <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}}
                        onClick={() => { setApproval(r); setApprovalError(''); setRefundMethod('transfer'); setRefundAccount(''); }}>Approve</button>
                      <button className="btn btn-danger btn-sm"
                        onClick={() => { setRejectModal(r.id); setRejectReason(''); }}>Reject</button>
                    </>
                  )}
                  {r.status==='pending' && (r.requested_by===user?.id || isSuperAdmin) && (
                    <button className="btn btn-ghost btn-sm" onClick={() => handleCancel(r.id)}>Cancel</button>
                  )}
                </div>
              </div>

              {/* Bank details */}
              {r.bank_name && (
                <div style={{background:'#F2EFE7',borderRadius:6,padding:'8px 12px',fontSize:12,marginTop:8}}>
                  <span style={{color:'#8A8A8A'}}>Refund to: </span>
                  <strong>{r.account_name}</strong> — {r.bank_name} | {r.account_number}
                </div>
              )}

              {/* Rejection reason */}
              {r.status==='rejected' && r.rejection_reason && (
                <div style={{background:'#FDEAEA',borderRadius:6,padding:'8px 12px',fontSize:12,marginTop:8,color:'#8F2E24'}}>
                  <strong>Rejection reason:</strong> {r.rejection_reason}
                </div>
              )}

              {/* Approval info */}
              {r.status==='approved' && r.approved_at && (
                <div style={{background:'#E8F5EB',borderRadius:6,padding:'8px 12px',fontSize:12,marginTop:8,color:'#2E6B3A'}}>
                  {r.financial_snapshot?.invoice_before && <div>Invoice value: {fmt(r.financial_snapshot.invoice_before.total)} → {fmt(r.financial_snapshot.invoice_after.total)}</div>}
                  <strong>Approved</strong> on {new Date(r.approved_at).toLocaleDateString('en-NG')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {approval && <div className="modal-overlay"><div className="modal" style={{maxWidth:480}}>
        <div className="modal-hdr"><span className="modal-title">Approve refund</span></div>
        <div className="modal-body">
          <p>Approve {fmt(approval.amount)} for {approval.reason}.</p>
          <p>{approval.financial_effect === 'excess_payment' ? 'The invoice price stays the same.' : 'The invoice price and net payment retained will both reduce.'} This records the refund payment as a cashbook outflow.</p>
          {approvalError && <div className="alert alert-error">{approvalError}</div>}
          <label className="form-label">Actual payment method *</label>
          <select className="form-control" value={refundMethod} onChange={e => { setRefundMethod(e.target.value); setRefundAccount(''); }} disabled={approvalBusy}>
            <option value="transfer">Bank transfer</option><option value="cash">Cash</option>
          </select>
          <label className="form-label">Paying account *</label>
          <select className="form-control" value={refundAccount} onChange={e => setRefundAccount(e.target.value)} disabled={approvalBusy}>
            <option value="">Select account</option>
            {bankAccounts.filter(a => a.is_active !== false && a.type === (refundMethod === 'cash' ? 'cash' : 'bank')).map(a => <option key={a.id} value={a.id}>{a.name || a.account_name || a.bank_name}</option>)}
          </select>
        </div>
        <div className="modal-ftr"><button className="btn btn-outline" disabled={approvalBusy} onClick={() => setApproval(null)}>Cancel</button>
          <button className="btn btn-primary" disabled={approvalBusy || !refundAccount} onClick={handleApprove}>{approvalBusy ? 'Approving...' : 'Approve and record refund'}</button></div>
      </div></div>}
      {/* Reject modal */}
      {rejectModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:420}}>
            <div className="modal-hdr">
              <span className="modal-title">Reject Refund Request</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setRejectModal(null)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Reason for Rejection *</label>
                <textarea className="form-control" rows={3} value={rejectReason}
                  onChange={e=>setRejectReason(e.target.value)}
                  placeholder="Explain why this refund is being rejected..." />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={() => setRejectModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleReject} disabled={!rejectReason}>
                Reject Refund
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
